/**
 * .gbox file format - Gotti Sandbox Project Format
 * 
 * This format is designed to be extensible. Each section is optional and
 * new sections can be added without breaking older exports. The format
 * auto-captures the entire sandbox state for perfect restoration.
 * 
 * Structure:
 * - version: Format version for compatibility
 * - meta: Metadata about the export (name, timestamp, etc.)
 * - files: The actual file contents organized by folder structure
 * - config: Any sandbox configuration (future: areas, settings, etc.)
 * - clients: Per-client settings for simulation
 */

/**
 * Version of the .gbox format. Increment when making breaking changes.
 */
export const GBOX_FORMAT_VERSION = 1;

/** Where a project's generated topology files sit — the project-relative paths of each (`TopologyPaths` in sandbox-core). */
export interface GboxProjectTopology {
  shared: string;
  clientGame: string;
  clientArea: string;
  serverGame: string;
  serverArea: string;
  clientRegistry: string;
  serverRegistry: string;
  clientEntry: string;
}

/** A sandbox project stored as a real Gotti project: every text file by its project-relative posix path, extension included. */
export interface GboxProject {
  files: Record<string, string>;
  /** Where the topology files are — a person's own file at a default path moves them to a suffixed one. */
  topology?: GboxProjectTopology;
  /** Every path the sandbox generated (root files, topology, registries): what the v1 view leaves out. */
  generated?: string[];
  /** The folders that hold no file (`src/client/components`) — a tree of files cannot hold them, the editor shows them. */
  folders?: string[];
  /** The section's format (`PROJECT_SECTION_FORMAT` in sandbox-core): a site refuses a section newer than it knows. */
  format?: number;
}

/**
 * File extension for sandbox exports
 */
export const GBOX_EXTENSION = '.gbox';

/**
 * MIME type for .gbox files
 */
export const GBOX_MIME_TYPE = 'application/x-gotti-sandbox';

/**
 * Default client settings - used when a setting is missing
 * This ensures forward compatibility when new settings are added
 */
export const DEFAULT_CLIENT_SETTINGS: Required<GboxClientSettings> = {
  simulatedPing: 50,
  simulatedPeerPing: null,
  packetLoss: 0,
  jitter: 0,
  // Add new default settings here - they'll automatically apply to old exports
};

/**
 * Per-client simulation settings
 * All fields are optional for forward compatibility - missing fields use defaults
 */
export interface GboxClientSettings {
  /** Simulated network latency to the server in milliseconds (default: 50) */
  simulatedPing?: number;
  /**
   * Simulated client-to-client (peer) latency in milliseconds.
   * `null` / omitted = inherit this client's {@link simulatedPing} (server
   * ping); a number sets an explicit peer ping independent of the server ping.
   */
  simulatedPeerPing?: number | null;
  /** Simulated packet loss percentage 0-100 (default: 0) */
  packetLoss?: number;
  /** Simulated jitter in milliseconds (default: 0) */
  jitter?: number;
  /** Allow any additional settings for forward compatibility */
  [key: string]: unknown;
}

/**
 * Client instance configuration for persistence
 */
export interface GboxClient {
  /** Unique client ID (e.g., "player-1") */
  id: string;
  /** Display name for the client */
  displayName?: string;
  /** Network simulation settings */
  settings: GboxClientSettings;
}

/**
 * Apply default settings to a client settings object
 * Ensures forward compatibility - new settings get defaults
 */
export function applyClientDefaults(settings?: GboxClientSettings): Required<GboxClientSettings> {
  return {
    ...DEFAULT_CLIENT_SETTINGS,
    ...settings,
  };
}

/**
 * Represents a single file in the sandbox
 */
export interface GboxFile {
  /** Filename without extension */
  name: string;
  /** File content */
  code: string;
  /** Language (default: typescript) */
  language?: string;
}

/**
 * A flexible folder structure that can contain:
 * - Root-level files (directly in the folder)
 * - Subfolders with their own files (arbitrarily nested via subfolder names like "foo/bar")
 */
export interface GboxFolder {
  /** Files at the root of this folder (e.g., api.ts, types.ts) */
  rootFiles: GboxFile[];
  /** Subfolders containing files (e.g., systems/, components/, custom/) */
  subfolders: Record<string, GboxFile[]>;
}

/**
 * File structure organized by top-level folder (client, server, shared)
 * Each folder can contain arbitrary subfolders and files
 */
export interface GboxFiles {
  client: GboxFolder;
  server: GboxFolder;
  shared: GboxFolder;
}

/**
 * Sandbox configuration settings
 */
export interface GboxConfig {
  /** Default number of clients to spawn */
  defaultClientCount?: number;
  /** Auto-run on load */
  autoRun?: boolean;
  /** Custom area definitions (future) */
  areas?: unknown[];
  /** Any additional config - allows forward compatibility */
  [key: string]: unknown;
}

/**
 * Metadata about the sandbox export
 */
export interface GboxMeta {
  /** Name of the sandbox project */
  name: string;
  /** Description */
  description?: string;
  /** When this was exported */
  exportedAt: string;
  /** Version of the sandbox app that created this */
  sandboxVersion: string;
  /** Author information */
  author?: string;
  /** Tags for categorization */
  tags?: string[];
}

/**
 * Complete .gbox file structure
 */
export interface GboxDocument {
  /** Format version for compatibility checking */
  version: number;
  /** Metadata about the export */
  meta: GboxMeta;
  /** All sandbox files organized by folder */
  files: GboxFiles;
  /**
   * The whole project as a real Gotti project (see `GboxProject`). Additive: `files` beside it is the same project's v1
   * view, so a reader that knows only `files` loads the person's files; a reader that knows `project` reads it instead.
   */
  project?: GboxProject;
  /** Sandbox configuration */
  config?: GboxConfig;
  /** 
   * Client instances with their settings (for multi-client scenarios)
   * If not provided, defaults to 1 client with default settings
   */
  clients?: GboxClient[];
  /**
   * Static assets (images, sounds, etc.) - references only
   * Full base64 data is included in exports, URLs are resolved on load
   */
  assets?: import('./assets').GboxAssets;
  /** 
   * Extension data - allows plugins/future features to store data
   * without modifying the core format 
   */
  extensions?: Record<string, unknown>;
}

/**
 * Create an empty GboxFolder structure
 */
export function createEmptyGboxFolder(): GboxFolder {
  return {
    rootFiles: [],
    subfolders: {},
  };
}

/**
 * Create an empty GboxFiles structure
 */
export function createEmptyGboxFiles(): GboxFiles {
  return {
    client: createEmptyGboxFolder(),
    server: createEmptyGboxFolder(),
    shared: createEmptyGboxFolder(),
  };
}

/**
 * Create a new GboxDocument with default values
 */
export function createGboxDocument(
  name: string,
  files: GboxFiles,
  options?: Partial<GboxMeta & GboxConfig>
): GboxDocument {
  return {
    version: GBOX_FORMAT_VERSION,
    meta: {
      name,
      description: options?.description,
      exportedAt: new Date().toISOString(),
      sandboxVersion: '0.0.1',
      author: options?.author,
      tags: options?.tags,
    },
    files,
    config: {
      defaultClientCount: options?.defaultClientCount ?? 1,
      autoRun: options?.autoRun ?? true,
    },
  };
}

/**
 * One runbook as stored in a gbox. Structurally mirrors sandbox-ai-v2's
 * `Runbook`, but kept loose here so sandbox-shared takes NO dependency on
 * sandbox-ai-v2 (this package is the dependency-free shared schema layer).
 */
export interface GboxRunbook {
  id: string;
  [key: string]: unknown;
}

const GOTTI_RUNBOOKS_KEY = 'gotti_runbooks';

/** Read the persisted runbooks out of `extensions.gotti_runbooks` ([] if absent). */
export function readGottiRunbooks(doc: GboxDocument): GboxRunbook[] {
  const ext = doc.extensions?.[GOTTI_RUNBOOKS_KEY] as
    | { version?: number; runbooks?: Record<string, GboxRunbook> }
    | undefined;
  if (!ext || typeof ext !== 'object' || !ext.runbooks) return [];
  return Object.values(ext.runbooks);
}

/** Return a NEW document with `runbooks` written into extensions.gotti_runbooks. */
export function writeGottiRunbooks(doc: GboxDocument, runbooks: GboxRunbook[]): GboxDocument {
  if (runbooks.length === 0) return doc;
  const map: Record<string, GboxRunbook> = {};
  for (const r of runbooks) map[r.id] = r;
  return {
    ...doc,
    extensions: { ...(doc.extensions ?? {}), [GOTTI_RUNBOOKS_KEY]: { version: 1, runbooks: map } },
  };
}
